import axios from 'axios';
import { api } from '../lib/api.js';
import type {
  FeatureFlags,
  TerminologyProfile,
  TerminologyTermKey,
} from '@attendance/shared/schemas/settings';

const API_ORIGIN = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

export type BrandingDateFormat = 'DD-MM-YYYY' | 'MM-DD-YYYY' | 'YYYY-MM-DD';
export type BrandingTimeFormat = '12h' | '24h';

export interface PublicTerminology {
  profile: TerminologyProfile;
  terms: Record<TerminologyTermKey, string>;
}

export interface PublicBranding {
  appName: string;
  primaryColor: string;
  logoUrl: string | null;
  /**
   * Tenant-configured date format pattern. Mirrors
   * `GeneralSettings.dateFormat` and is surfaced publicly so the unauth'd
   * login screen (and `useFormatDate`) can render dates consistently before
   * the user is signed in.
   */
  dateFormat?: BrandingDateFormat;
  /**
   * Tenant-configured clock format ('12h' / '24h'). Surfaced publicly so
   * `useFormatTime` renders times per the General setting instead of the
   * browser locale default.
   */
  timeFormat?: BrandingTimeFormat;
  /**
   * The tenant's IANA zone (`general.timezone`, mirrored on `Company.timezone`)
   * — the zone that decides where one working day ends and the next begins.
   *
   * Surfaced here, on the payload every client already bootstraps, because it
   * is the ONLY basis on which the client's "today" agrees with the server's
   * rollups, and because `/settings/general` is ADMIN/HR-only while the screens
   * that need it (leave request, my-leave) are employee-facing. Read it through
   * `useCompanyTimeZone()` / `useCompanyToday()`, never directly.
   *
   * Optional: an older API build, a failed bootstrap, or a pre-install tenant
   * leaves it absent, and the helpers fall back to the same `Asia/Dubai` the
   * server's `safeTimeZone` does.
   */
  timezone?: string;
  /** Tenant terminology (profile + resolved term set). Absent on legacy/pre-install. */
  terminology?: PublicTerminology;
  /** Enabled feature modules. Absent on legacy/pre-install (treat as all on). */
  features?: FeatureFlags;
}

export const brandingApi = {
  /**
   * Public — works without auth and even before install completes
   * (returns null then).
   */
  async getPublic(): Promise<PublicBranding | null> {
    try {
      const { data } = await axios.get<{ data: PublicBranding | null }>(
        `${API_ORIGIN}/api/branding`
      );
      return data.data;
    } catch {
      return null;
    }
  },

  uploadLogo: async (file: File): Promise<{ logoUrl: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const result = await api.post<{ data: { logoUrl: string } }>(
      '/settings/upload-logo',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return result.data;
  },
};

/**
 * URL the browser hits to fetch the current logo (if any).
 * Adding a cache-buster on save keeps the new logo visible immediately.
 */
export function logoUrl(version?: string | number): string {
  const base = `${API_ORIGIN}/api/branding/logo`;
  return version ? `${base}?v=${version}` : base;
}
